import React, { useEffect, useMemo, useState } from 'react'
import SearchField from '../SearchField'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem,
  Radio, RadioGroup, Select, Switch, TextField, Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import GetAppIcon from '@material-ui/icons/GetApp'
import { toast } from '../../api/toast'
import api from '../../api/client'
import { toFarsiDigits } from '../../assets/utils'

const MODES = [
  { value: 'normal', label: 'عادی — بدون زیرجمع، همه در یک صفحه' },
  { value: 'page', label: 'تفکیک صفحه‌ای — هر نوع صورت‌وضعیت در صفحهٔ جدا' },
  { value: 'subtotal', label: 'زیرجمع — زیرجمع هر نوع + هر قرارداد + جمع کل' },
]

/**
 * دیالوگ گزارش مالی تجمیعی: قرارداد → نوع صورت‌وضعیت → صورت‌وضعیت
 * امکانات: فیلتر نوع/شماره، انتخاب قرارداد، حذف قراردادهای بدون مانده
 */
const GroupedReportDialog = ({ open, onClose, contracts }) => {
  const [types, setTypes] = useState([])
  const [numbers, setNumbers] = useState([])
  const [selTypes, setSelTypes] = useState([])
  const [selNumbers, setSelNumbers] = useState([])
  const [selContracts, setSelContracts] = useState([])  // [] = همه
  const [hideZero, setHideZero] = useState(false)       // حذف قراردادهای بدون مانده
  const [mode, setMode] = useState('subtotal')
  const [numSearch, setNumSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(null)

  // قراردادهای مجاز (فقط id و number و contractee برای نمایش)
  const allContracts = useMemo(
    () => (contracts || []).map((c) => ({ id: c.id, number: c.number, contractee: c.contractee })),
    [contracts]
  )
  const contractIds = useMemo(
    () => (selContracts.length ? selContracts : allContracts.map((c) => c.id)).join(','),
    [selContracts, allContracts]
  )

  useEffect(() => {
    if (!open || !contractIds) return
    setLoading(true)
    setSelTypes([])
    setSelNumbers([])
    setNumSearch('')
    api.get('contracts/report/grouped/filters/', { params: { contract_ids: contractIds } })
      .then((res) => {
        setTypes(res.data.types || [])
        setNumbers(res.data.numbers || [])
      })
      .catch(() => { setTypes([]); setNumbers([]) })
      .finally(() => setLoading(false))
  }, [open, contractIds])

  const toggle = (arr, setArr, v) => {
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v])
  }

  const params = () => ({
    contract_ids: contractIds,
    types: selTypes.join(','),
    request_numbers: selNumbers.join(','),
    mode,
    hide_zero: hideZero ? '1' : '0',
  })

  const download = async (kind) => {
    setDownloading(kind)
    try {
      const res = await api.get(`contracts/report/grouped/${kind}/`, {
        params: params(),
        responseType: 'blob',
      })
      const ext = kind === 'pdf' ? 'pdf' : 'xlsx'
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `financial_report.${ext}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e) {
      const txt = e?.response?.data
      toast(typeof txt === 'string' && txt ? txt : 'خطا در ساخت گزارش', 'error')
    } finally {
      setDownloading(null)
    }
  }

  const filteredNumbers = numbers.filter((n) =>
    !numSearch.trim() || toFarsiDigitSafe(n.number).includes(toFarsiDigitSafe(numSearch.trim()))
  )

  const scopeLabel = (selContracts.length || allContracts.length)
    ? `${toFarsiDigits(selContracts.length || allContracts.length)} قرارداد${allContracts[0] ? ' — ' + allContracts[0].contractee : ''}`
    : '—'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ fontWeight: 800, color: '#1a237e' }}>
        📗 گزارش مالی تجمیعی
      </DialogTitle>
      <DialogContent>
        <Typography style={{ fontSize: 12.5, color: '#5c6370', marginBottom: 10 }}>
          دامنه: {scopeLabel} — گروه‌بندی: قرارداد → نوع صورت‌وضعیت → صورت‌وضعیت
        </Typography>

        {/* انتخاب قرارداد */}
        {allContracts.length > 1 && (
          <>
            <Typography style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
              قرارداد ({toFarsiDigits(allContracts.length)} مورد — بدون تیک = همه)
            </Typography>
            <Box style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #e3e6f2', borderRadius: 8, padding: 8, background: '#fbfcff', marginBottom: 10 }}>
              <Chip
                size="small" label={selContracts.length === 0 ? '✓ همه' : 'همه'}
                color={selContracts.length === 0 ? 'primary' : 'default'}
                variant={selContracts.length === 0 ? 'default' : 'outlined'}
                onClick={() => setSelContracts([])}
                style={{ fontWeight: 700, marginLeft: 4 }}
              />
              {allContracts.map((c) => (
                <Chip
                  key={c.id}
                  size="small"
                  label={`#${c.number}`}
                  color={selContracts.includes(c.id) ? 'primary' : 'default'}
                  onClick={() => toggle(selContracts, setSelContracts, c.id)}
                  style={{ fontWeight: 700, margin: 4 }}
                />
              ))}
            </Box>
          </>
        )}

        {/* حذف قراردادهای بدون مانده */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              color="primary"
              checked={hideZero}
              onChange={(e) => setHideZero(e.target.checked)}
            />
          }
          label={
            <Typography style={{ fontSize: 12.5 }}>
              حذف قراردادهای بدون مانده (صفر)
            </Typography>
          }
          style={{ marginBottom: 10, display: 'block' }}
        />

        <Divider style={{ margin: '6px 0 10px' }} />

        {/* نوع صورت‌وضعیت */}
        <Typography style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>نوع صورت‌وضعیت</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" marginBottom={1}>
          <Chip
            size="small"
            label={selTypes.length === 0 ? '✓ همه' : 'همه'}
            color={selTypes.length === 0 ? 'primary' : 'default'}
            variant={selTypes.length === 0 ? 'default' : 'outlined'}
            onClick={() => setSelTypes([])}
            style={{ fontWeight: 700 }}
          />
          {types.map((t) => (
            <Chip
              key={t}
              size="small"
              label={t}
              color={selTypes.includes(t) ? 'primary' : 'default'}
              onClick={() => toggle(selTypes, setSelTypes, t)}
              style={{ fontWeight: 700 }}
            />
          ))}
          {types.length === 0 && !loading && (
            <Typography style={{ fontSize: 12, color: '#9aa1b9' }}>نوعی یافت نشد</Typography>
          )}
        </Box>

        {/* صورت‌وضعیت (شماره) */}
        <Typography style={{ fontWeight: 700, fontSize: 13, margin: '10px 0 6px' }}>
          صورت‌وضعیت ({toFarsiDigits(numbers.length)} مورد — برای انتخاب قسمتی، تیک بزنید؛ بدون تیک = همه)
        </Typography>
        <SearchField
          size="small" fullWidth variant="outlined"
          placeholder="جستجو در شماره صورت‌وضعیت..."
          value={numSearch}
          onChange={(e) => setNumSearch(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <Box style={{ maxHeight: 170, overflowY: 'auto', border: '1px solid #e3e6f2', borderRadius: 8, padding: 8, background: '#fbfcff' }}>
          <Chip
            size="small" label={selNumbers.length === 0 ? '✓ همه' : 'همه'}
            color={selNumbers.length === 0 ? 'primary' : 'default'}
            variant={selNumbers.length === 0 ? 'default' : 'outlined'}
            onClick={() => setSelNumbers([])}
            style={{ fontWeight: 700, marginLeft: 4 }}
          />
          {filteredNumbers.map((n) => (
            <Chip
              key={n.number + '|' + n.type}
              size="small"
              label={`${n.number} (${n.type})`}
              color={selNumbers.includes(n.number) ? 'primary' : 'default'}
              onClick={() => toggle(selNumbers, setSelNumbers, n.number)}
              style={{ fontWeight: 700, margin: 4 }}
            />
          ))}
          {filteredNumbers.length === 0 && (
            <Typography style={{ fontSize: 12, color: '#9aa1b9', padding: 6 }}>
              شماره‌ای یافت نشد
            </Typography>
          )}
        </Box>

        <Divider style={{ margin: '14px 0 10px' }} />

        {/* حالت خروجی */}
        <Typography style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>حالت گزارش</Typography>
        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES.map((m) => (
            <FormControlLabel key={m.value} value={m.value} control={<Radio size="small" color="primary" />} label={
              <span style={{ fontSize: 12 }}>{m.label}</span>
            } />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions style={{ padding: '12px 16px' }}>
        <Button onClick={onClose} disabled={downloading !== null}>انصراف</Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PictureAsPdfIcon />}
          disabled={downloading !== null}
          onClick={() => download('pdf')}
        >
          {downloading === 'pdf' ? 'در حال ساخت...' : 'دانلود PDF'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<GetAppIcon />}
          disabled={downloading !== null}
          onClick={() => download('excel')}
        >
          {downloading === 'excel' ? 'در حال ساخت...' : 'دانلود اکسل'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function toFarsiDigitSafe(v) {
  return String(v == null ? '' : v)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
}

export default GroupedReportDialog
